import React from 'react';
import { Link } from 'react-router-dom';
import { useMyList, MAX_LIST_SIZE } from '../hooks/useMyList';
import Header from './Header';
import Footer from './Footer';
import Skeleton from './Skeleton';
import PlaceAutocomplete, { PlaceSelection } from './PlaceAutocomplete';

const MyListPage: React.FC = () => {
  const { list, isLoaded, error, add, remove, move } = useMyList();

  // No in-flight gating here: useMyList serializes every mutation
  // internally, so quick successive actions (including a burst of adds)
  // queue up safely instead of racing or being dropped. Disabling the
  // search box mid-save was how picks used to vanish silently.
  const canAdd = list.length < MAX_LIST_SIZE;

  const handleSelect = (place: PlaceSelection) => {
    void add(place);
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header backTo="/" backLabel="All Restaurants" />

      <main className="flex-1 container mx-auto px-6 pt-12 pb-20 max-w-3xl">
        <div className="mb-10 animate-fade-in">
          <h1 className="font-display text-4xl md:text-5xl text-charcoal mb-3 tracking-tight">
            My Top Five
          </h1>
          <p className="text-charcoal-light font-light">
            Rankum runs on full top fives, so pick all {MAX_LIST_SIZE} of your
            favorites. Search for any restaurant on Google Maps; your list syncs
            across devices.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="card mb-6 p-4 border-terracotta/30 bg-terracotta/[0.06] text-sm text-terracotta"
          >
            {error}
          </div>
        )}

        {isLoaded && list.length < MAX_LIST_SIZE && (
          // The one loud tell that an unfinished list is private. The gate
          // itself lives in lib/fullList + the aggregated_picks view; this
          // notice is how the owner finds out about it, so it stays
          // prominent (a filled card, not a line of muted text).
          <div
            role="status"
            className="card mb-6 p-4 border-amber/40 bg-amber/[0.08]"
          >
            <p className="text-sm text-charcoal">
              <span className="font-semibold">
                Your list isn&rsquo;t public yet.
              </span>{' '}
              {list.length === 0
                ? 'Pick your five favorites and it goes live on the site.'
                : `Pick ${MAX_LIST_SIZE - list.length} more and it goes live on the site.`}
            </p>
          </div>
        )}

        {!isLoaded ? (
          <div aria-busy="true" aria-label="Loading your picks">
            <Skeleton className="h-6 w-32 mb-4" />
            <ol className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="card p-4 flex items-center gap-4">
                  <Skeleton className="h-10 w-8 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-1/2" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Skeleton className="h-8 w-8 rounded" />
                    <Skeleton className="h-8 w-8 rounded" />
                    <Skeleton className="h-8 w-8 rounded" />
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <>
            <section className="mb-12">
              <h2 className="font-display text-2xl text-charcoal mb-4">
                Your picks{' '}
                {list.length > 0 && (
                  <span className="text-charcoal-light text-base">
                    ({list.length}/{MAX_LIST_SIZE})
                  </span>
                )}
              </h2>
              {/* The incomplete case is covered by the louder not-public
                  notice above; repeating a quieter countdown here just
                  diluted it. */}
              {list.length === MAX_LIST_SIZE && (
                <p className="text-sm text-charcoal-light mb-4" role="status">
                  Your top five is complete and live on the site.
                </p>
              )}
              {list.length === 0 ? (
                error ? null : (
                  <div className="card p-6 text-charcoal-light">
                    No picks yet. Search below to add your first restaurant.
                  </div>
                )
              ) : (
                <ol className="space-y-3">
                  {list.map((pick, index) => {
                    return (
                      <li key={pick.placeId} className="card p-4 flex items-center gap-4">
                        <span className="font-display text-3xl md:text-4xl leading-none text-amber w-8 shrink-0 text-center">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/place/${pick.placeId}`}
                            className="font-display text-lg text-charcoal hover:text-amber transition-colors duration-300 block truncate"
                          >
                            {pick.name}
                          </Link>
                          {(pick.cuisine || pick.location) && (
                            <p className="text-sm text-charcoal-light truncate">
                              {[pick.cuisine, pick.location].filter(Boolean).join(' · ')}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            disabled={index === 0}
                            onClick={() => void move(pick.placeId, 'up')}
                            className="p-2 text-charcoal-light hover:text-amber disabled:opacity-30 disabled:hover:text-charcoal-light transition-colors"
                            aria-label={`Move ${pick.name} up`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            disabled={index === list.length - 1}
                            onClick={() => void move(pick.placeId, 'down')}
                            className="p-2 text-charcoal-light hover:text-amber disabled:opacity-30 disabled:hover:text-charcoal-light transition-colors"
                            aria-label={`Move ${pick.name} down`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => void remove(pick.placeId)}
                            className="p-2 text-charcoal-light hover:text-terracotta disabled:opacity-30 transition-colors"
                            aria-label={`Remove ${pick.name}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            <section>
              {canAdd ? (
                <>
                  <h2 className="font-display text-2xl text-charcoal mb-4">
                    Add a restaurant
                  </h2>
                  <PlaceAutocomplete
                    onSelect={handleSelect}
                    placeholder="Search any restaurant on Google Maps"
                  />
                </>
              ) : (
                <p className="text-charcoal-light">
                  To add a new restaurant, remove one above first.
                </p>
              )}
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default MyListPage;
